/**
 * Local useSelector / useDispatch / useStore shim for react-redux 6.x.
 *
 * The editor cleanup PR replaced the few `connect()` HOCs with the
 * idiomatic hooks API, but this codebase ships react-redux 6.0.1 which
 * predates `useSelector` / `useDispatch` (added in 7.1). Rather than
 * pulling in the dep bump, this file reads `ReactReduxContext`
 * directly and rebuilds a minimal hooks API on top.
 *
 * The implementation mirrors react-redux 7's core behaviour:
 *   • Selectors run on every render against the latest state.
 *   • A subscription forces a re-render whenever the selected slice
 *     changes (default reference equality).
 *   • Updates batch naturally because React schedules state updates.
 */

import { useContext, useLayoutEffect, useReducer, useRef } from 'react';
import { ReactReduxContext } from 'react-redux';

type AnyState = any;

function useStoreContext() {
  const ctx = useContext(ReactReduxContext as any) as {
    store: { getState: () => AnyState; subscribe: (cb: () => void) => () => void; dispatch: any };
  } | null;
  if (!ctx || !ctx.store) {
    throw new Error(
      'reactReduxHooks: no <Provider> found upstream — wrap your tree in react-redux <Provider>.',
    );
  }
  return ctx.store;
}

export function useStore() {
  return useStoreContext();
}

export function useDispatch<T = any>(): T {
  return useStoreContext().dispatch as T;
}

export function useSelector<TState = AnyState, TResult = unknown>(
  selector: (state: TState) => TResult,
  equalityFn: (a: TResult, b: TResult) => boolean = Object.is,
): TResult {
  const store = useStoreContext();
  const [, forceRender] = useReducer((c: number) => c + 1, 0);
  const lastSelectedRef = useRef<TResult | undefined>(undefined);
  const lastStoreStateRef = useRef<TState | undefined>(undefined);
  const lastSelectorRef = useRef(selector);
  const errorRef = useRef<Error | null>(null);

  let selected: TResult;
  try {
    if (selector !== lastSelectorRef.current || lastStoreStateRef.current !== store.getState()) {
      selected = selector(store.getState());
    } else {
      selected = lastSelectedRef.current as TResult;
    }
  } catch (err) {
    errorRef.current = err as Error;
    throw err;
  }

  useLayoutEffect(() => {
    lastSelectorRef.current = selector;
    lastStoreStateRef.current = store.getState();
    lastSelectedRef.current = selected;
    errorRef.current = null;
  });

  useLayoutEffect(() => {
    function checkForUpdates() {
      try {
        const newState = store.getState();
        const currentSelector = lastSelectorRef.current;
        const next = currentSelector ? currentSelector(newState) : (undefined as any);
        if (equalityFn(next, lastSelectedRef.current as TResult)) {
          return;
        }
        lastSelectedRef.current = next;
        lastStoreStateRef.current = newState;
      } catch (err) {
        errorRef.current = err as Error;
      }
      forceRender();
    }
    checkForUpdates();
    return store.subscribe(checkForUpdates);
    // store + equalityFn are stable during a Provider's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  return selected;
}
